import * as React from 'react';
import { ChangeEvent } from 'react';

import { Button, ButtonContainer, Select as EmbarkSelect, Link, Tabs, Typography } from '@emburse/ui-core';

import { ConfirmModal } from '@ReactComponents/ConfirmModal';
import { Select } from '@ReactComponents/Select';
import { SimplePagination } from '@ReactComponents/Shared/Pagination';

import { IUserExtension } from '@ClientModels/userExtension';
import { AMEX_ISSUED_CARDS } from '@Shared/constants';
import { ILinkedAccountModel } from '@Shared/models';

import * as style from './ManageCardsPanel.styl';

const PAGE_SIZE = 50;

interface IManageCardsPanelProps {
  cards: ILinkedAccountModel[];
  users: IUserExtension[];
  usersByCardId: {
    [key: string]: IUserExtension;
  };
  cancelCard: (card: ILinkedAccountModel) => void;
  deleteCard: (card: ILinkedAccountModel) => void;
  closeModal: () => void;
  removeUser: (card: ILinkedAccountModel) => void;
  save: () => void;
  showBulkSkipView: () => void;
  updateCardUser: (user: IUserExtension, card: ILinkedAccountModel) => void;
}

type TabNames = 'unassigned' | 'assigned' | 'canceled' | 'deleted';

interface IManageCardsPanelState {
  visibleCards: ILinkedAccountModel[];
  currentTab: TabNames;
  page: number;
  isConfirmModalOpen?: boolean;
  selectedCard?: ILinkedAccountModel;
}

export class ManageCardsPanel extends React.Component<IManageCardsPanelProps, IManageCardsPanelState> {
  state: IManageCardsPanelState = {
    visibleCards: [],
    currentTab: 'unassigned',
    page: 0
  };

  componentDidMount() {
    const unassignedCards = this.props.cards.filter((card) => !card.user_id && card.state !== 'deleted');

    return this.filterShownCards(unassignedCards, 'unassigned');
  }

  componentDidUpdate(prevProps: Readonly<IManageCardsPanelProps>, prevState: Readonly<IManageCardsPanelState>, snapshot?: any) {
    const hasNewCancelledCard =
      this.props.cards.filter((card) => card.state === 'canceled').length !==
      prevProps.cards.filter((card) => card.state === 'canceled').length;

    if (hasNewCancelledCard) {
      this.setState({
        visibleCards: this.getUpdatedSortedCards(this.props.cards, this.state.visibleCards)
      });
    }
  }

  setPage = (newPage: number) => this.setState({ page: newPage });

  getUpdatedSortedCards(newCards: ILinkedAccountModel[], sortedCards: ILinkedAccountModel[]) {
    return sortedCards
      .map((sortedCard) => {
        return newCards.find((card) => card.id === sortedCard.id);
      })
      .filter(Boolean);
  }

  filterShownCards(cards: ILinkedAccountModel[], newTabName: TabNames) {
    const sortedCards = cards.sort((a, b) => {
      if (a.state === 'canceled') {
        return 1;
      }
      if (b.state === 'canceled') {
        return -1;
      }
      return 0;
    });

    return this.setState({
      visibleCards: sortedCards,
      currentTab: newTabName,
      page: 0
    });
  }

  isReassignCardDisabled(card: ILinkedAccountModel) {
    if (window.FEATURES.disable_reassign_virtual_cards === true || AMEX_ISSUED_CARDS.includes(card.institution_type as string)) {
      const { currentTab } = this.state;

      if (currentTab === 'unassigned') {
        return false; // users can always assign card in Unassigned tab
      }

      return card.is_virtual_card;
    }
    return false;
  }

  renderUserSelect(users: IUserExtension[], userForCard: IUserExtension, card: ILinkedAccountModel) {
    return (
      <Select
        className="grow-1"
        options={users}
        getOptionLabel={(user) => user.displayName}
        getOptionValue={(user) => user.id}
        value={userForCard}
        onChange={(option: IUserExtension) => this.props.updateCardUser(option, card)}
      />
    );
  }

  renderEmbarkUserSelect(users: IUserExtension[], userForCard: IUserExtension, card: ILinkedAccountModel) {
    return (
      <EmbarkSelect
        disabled={this.isReassignCardDisabled(card)}
        value={userForCard ? userForCard.id : ''}
        id={`user-select-${card.id}`}
        className="grow-1"
        label="User"
        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
          const selectedUser = users.find((user) => user.id === event.target.value);

          if (selectedUser) {
            return this.props.updateCardUser(selectedUser, card);
          }

          this.props.removeUser(card);
        }}
        native
      >
        <option aria-label="none" value="" />
        {users.map((user, key) => (
          <option key={key} value={user.id}>
            {user.displayName}
          </option>
        ))}
      </EmbarkSelect>
    );
  }

  renderCardRow(card: ILinkedAccountModel, userForCard?: IUserExtension) {
    const { users } = this.props;
    const isCardName = Boolean(card.name);
    const isCardNumber = Boolean(card.meta && card.meta.number);
    const cardId = card.id;

    const cardIsEmburseViaGateway = card.provider === 'emburse_cards_gateway' && card.institution_type === 'emburse';
    const cardIsDeleted = card.state === 'deleted';
    const showCancelButton = cardIsEmburseViaGateway && card.state === 'active';
    const showDeleteButton = !showCancelButton && !cardIsDeleted && !cardIsEmburseViaGateway; // We do not support deletion of Emburse cards at this time.
    const showRemoveUserButton = Boolean(card.user_id);

    return (
      <div key={cardId} className="cf card flex jc-fs ai-c">
        <div>
          <div className={'card-name' + (isCardName ? '' : ' italic')}>
            {<span data-rh={card.meta && card.meta.number}>{isCardName ? card.name : 'Unnamed'}</span>}
            {isCardNumber && <span> - {card.meta.number}</span>}
          </div>
          {card.state === 'canceled' && <span className="canceled-label">Canceled</span>}
        </div>
        <div className="card-controls grow-1">
          <div className="flex ai-c">
            {!cardIsDeleted && this.renderUserSelect(users, userForCard, card)}
            {cardIsDeleted && <span>This card was deleted</span>}
            {showDeleteButton && (
              <span className="card-action" data-rh="Delete and unassign card" onClick={() => this.props.deleteCard(card)}>
                <span className="icon icon-trash" />
              </span>
            )}
            {showCancelButton && (
              <span className="card-action" data-rh="Cancel card" onClick={() => this.props.cancelCard(card)}>
                <span className="icon icon-minus-circle" />
              </span>
            )}
          </div>
          {showRemoveUserButton && (
            <div className="fakeLink" onClick={() => this.props.removeUser(card)}>
              Remove user
            </div>
          )}
        </div>
      </div>
    );
  }

  renderEmbarkCardRow(card: ILinkedAccountModel, userForCard?: IUserExtension) {
    const { users } = this.props;
    const isCardName = Boolean(card.name);
    const isCardNumber = Boolean(card.meta && card.meta.number);
    const cardId = card.id;

    const cardIsEmburseViaGateway = card.provider === 'emburse_cards_gateway' && card.institution_type === 'emburse';
    const cardIsDeleted = card.state === 'deleted';
    const showCancelButton = cardIsEmburseViaGateway && card.state === 'active';
    const showDeleteButton = !showCancelButton && !cardIsDeleted && !cardIsEmburseViaGateway && !this.isReassignCardDisabled(card); // We do not support deletion of Emburse cards at this time.
    const showRemoveUserButton = Boolean(card.user_id) && !this.isReassignCardDisabled(card);

    return (
      <div key={cardId} className="card-body">
        <div className="vertical-gap" />
        <div className="cf card flex jc-fs ai-c fw-nw">
          <div>
            <div className={'card-name' + (isCardName ? '' : ' italic')}>
              {<span data-rh={card.meta && card.meta.number}>{isCardName ? card.name : 'Unnamed'}</span>}
              {isCardNumber && <span> - {card.meta.number}</span>}
            </div>
            {card.state === 'canceled' && <span className="canceled-label">Canceled</span>}
          </div>
          <div className="card-controls grow-1">
            <div className="flex ai-c fw-nw">
              {!cardIsDeleted && this.renderEmbarkUserSelect(users, userForCard, card)}
              {cardIsDeleted && <span>This card was deleted</span>}
              {showDeleteButton && (
                <span
                  className="card-action"
                  data-rh="Delete and unassign card"
                  onClick={() =>
                    this.setState({
                      isConfirmModalOpen: true,
                      selectedCard: card
                    })
                  }
                >
                  <span className="icon icon-trash" />
                </span>
              )}
              {showCancelButton && (
                <span className="card-action" data-rh="Cancel card" onClick={() => this.props.cancelCard(card)}>
                  <span className="icon icon-minus-circle" />
                </span>
              )}
            </div>
            {showRemoveUserButton && (
              <div className="fakeLink" onClick={() => this.props.removeUser(card)}>
                Remove user
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderEmbarkDescription(showBulkSkipView: () => void) {
    return (
      <div className="card-body">
        <Typography>
          Map the following cards to the corresponding card holders. Keep in mind that brand new cards won’t show up until they have at
          least one posted transaction.
        </Typography>
        <Typography>
          Need to reset transactions? <Link onClick={showBulkSkipView}>Bulk skip</Link>
        </Typography>
      </div>
    );
  }

  renderDescription(showBulkSkipView: () => void) {
    return (
      <div className="blurb">
        <p>
          Map the following cards to the corresponding card holders. Keep in mind that brand new cards won’t show up until they have at
          least one posted transaction.
        </p>
        <p>
          Need to reset transactions?{' '}
          <a className="text-link" onClick={showBulkSkipView}>
            Bulk skip
          </a>
        </p>
      </div>
    );
  }

  renderEmbarkTabs({
    unassignedCards,
    assignedCards,
    canceledCards,
    deletedCards
  }: {
    unassignedCards: ILinkedAccountModel[];
    assignedCards: ILinkedAccountModel[];
    canceledCards: ILinkedAccountModel[];
    deletedCards: ILinkedAccountModel[];
  }) {
    const tabs: Array<{ label: string; value: TabNames; cards: ILinkedAccountModel[] }> = [
      { label: `Un-Assigned (${unassignedCards.length})`, value: 'unassigned', cards: unassignedCards },
      { label: `Assigned (${assignedCards.length})`, value: 'assigned', cards: assignedCards },
      { label: `Canceled (${canceledCards.length})`, value: 'canceled', cards: canceledCards },
      { label: `Deleted (${deletedCards.length})`, value: 'deleted', cards: deletedCards }
    ];

    return (
      <div className="card-tabs">
        <Tabs
          id="abaTabs"
          variant="scrollable"
          tabs={tabs}
          onChange={(_: ChangeEvent, tabNr: number) => {
            this.filterShownCards(tabs[tabNr].cards, tabs[tabNr].value);
          }}
        />
      </div>
    );
  }

  renderTabs(
    {
      unassignedCards,
      assignedCards,
      canceledCards,
      deletedCards
    }: {
      unassignedCards: ILinkedAccountModel[];
      assignedCards: ILinkedAccountModel[];
      canceledCards: ILinkedAccountModel[];
      deletedCards: ILinkedAccountModel[];
    },
    currentTab: TabNames
  ) {
    return (
      <ul className="abaTab">
        <li className={currentTab === 'unassigned' ? 'active' : ''} onClick={() => this.filterShownCards(unassignedCards, 'unassigned')}>
          Un-Assigned ({unassignedCards.length})
        </li>
        <li className={currentTab === 'assigned' ? 'active' : ''} onClick={() => this.filterShownCards(assignedCards, 'assigned')}>
          Assigned ({assignedCards.length})
        </li>
        {
          <li className={currentTab === 'canceled' ? 'active' : ''} onClick={() => this.filterShownCards(canceledCards, 'canceled')}>
            Canceled ({canceledCards.length})
          </li>
        }
        {
          <li className={currentTab === 'deleted' ? 'active' : ''} onClick={() => this.filterShownCards(deletedCards, 'deleted')}>
            Deleted ({deletedCards.length})
          </li>
        }
      </ul>
    );
  }

  renderFooter() {
    return (
      <div className="cf flex jc-fe buttonWrap">
        <div className="abaButton tertiary" onClick={() => this.props.closeModal()}>
          Cancel
        </div>
        <div className="abaButton bLeftPad" onClick={() => this.props.save()}>
          Save
        </div>
      </div>
    );
  }

  renderEmbarkFooter() {
    return (
      <ButtonContainer alignButtons="right" className="card-footer">
        <Button variant="outlined" onClick={() => this.props.closeModal()} color="secondary">
          Cancel
        </Button>
        <Button onClick={() => this.props.save()}>Save</Button>
      </ButtonContainer>
    );
  }

  renderConfirmModal(card: ILinkedAccountModel) {
    return (
      <ConfirmModal
        headerText="Confirm Action"
        onConfirm={() => {
          this.props.deleteCard(card);
          this.setState({ isConfirmModalOpen: false });
        }}
        confirmText="Confirm Delete"
        showClose={true}
        closeModal={() => this.setState({ isConfirmModalOpen: false })}
      >
        <Typography>Are you sure you want to delete and unassign this card? This action cannot be undone.</Typography>
      </ConfirmModal>
    );
  }

  render() {
    const { showBulkSkipView, usersByCardId } = this.props;
    const cards = this.props.cards || [];
    const { visibleCards } = this.state;
    const assignedCards: ILinkedAccountModel[] = [];
    const unassignedCards: ILinkedAccountModel[] = [];
    const deletedCards: ILinkedAccountModel[] = [];
    const canceledCards: ILinkedAccountModel[] = [];

    cards.map((card) => {
      // We do not want to show deleted cards on any tab except for the "Deleted" tab, so we must make a note of whether a card is in such a state.
      const isDeleted = card.state === 'deleted';

      if (card.user_id && !isDeleted) {
        assignedCards.push(card);
      }
      if (!card.user_id && !isDeleted) {
        unassignedCards.push(card);
      }
      if (card.state === 'canceled') {
        canceledCards.push(card);
      }
      if (isDeleted) {
        deletedCards.push(card);
      }
    });

    return (
      <div className={style.global}>
        {this.renderEmbarkDescription(showBulkSkipView)}
        <div className="cards">
          {this.renderEmbarkTabs({
            assignedCards,
            canceledCards,
            deletedCards,
            unassignedCards
          })}
          <div className="cards-container">
            {visibleCards &&
              visibleCards
                .slice(this.state.page * PAGE_SIZE, this.state.page * PAGE_SIZE + PAGE_SIZE)
                .map((card) => this.renderEmbarkCardRow(card, usersByCardId[card.id]))}
            <SimplePagination
              page={this.state.page}
              pageSize={PAGE_SIZE}
              count={visibleCards.length}
              zeroBased
              onPageChange={this.setPage}
            />
          </div>
          {this.renderEmbarkFooter()}
        </div>
        {this.state.isConfirmModalOpen && this.renderConfirmModal(this.state.selectedCard)}
      </div>
    );
  }
}
